/**
 * PptxViewerInline — Ando Slides review surface.
 *
 * What we do (and nobody else does for .pptx):
 *  - Render the slide deck as PDF (server-side via LibreOffice headless)
 *    — same render path as `OfficePreview`, kept SSR-stable.
 *  - Overlay an "AI proposed changes" highlight on top of the slide
 *    canvas: each highlight carries a `bbox` (slide-relative coords
 *    in `[0..1]`) that we paint as an orange-tinted rectangle anchored
 *    to the rendered slide.
 *  - Speaker-notes pane below the slide (read-only for now — editable
 *    in phase 2).
 *  - Slide navigation (◄ N/M ► + dots) with keyboard (←/→/PgUp/PgDn)
 *    and click-jump from the review panel.
 *
 * Why this is novel:
 *  - PowerPoint Online doesn't expose "highlight the shape" overlays.
 *  - Univer's Slides facade is empty.
 *  - OnlyOffice ribbon-mode forces the editor metaphor; review feels alien.
 *  Ando's slide review surface treats slides as DOCUMENTS (read+annotate),
 *  not as canvases-to-edit-directly. The visual mode matches how people
 *  ACTUALLY consume pptx → "I scrolled through, I see what Ando wants
 *  to change".
 *
 * Wiring contract (May 24, 2026 unification):
 *  - Single source of truth = the active ``action_proposal`` for the open
 *    pptx (BE-canonical ``ActionProposal.document_edit.changes`` array,
 *    each entry enriched with a normalised ``bbox`` server-side via
 *    ``pptx_bbox_service.locate_pptx_target``).
 *  - Parent (`ArtifactPane`) derives `highlights` + `speakerNotesBySlide`
 *    from the action via `deriveSlideHighlights` / `deriveSpeakerNotes`
 *    helpers in `reviewSource.ts`.
 *  - The same action also drives `createPptxReviewSource` for the
 *    `AISummaryPanel` right-rail cards.
 *  - The imperative `scrollToProposal(id)` handle lets the review panel
 *    centre a specific change in view on card click.
 */

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { forwardRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Pencil,
  Sparkles,
  StickyNote,
} from "lucide-react";

import { Button } from "../primitives/button";
import { cn } from "../lib/cn";
import { AndoBrainLoader } from "../ando/blob/AndoBrainLoader";
import type { EditSession } from "./EditSession";
import { fetchSessionBytes } from "./fetchSessionBytes";

/** OOXML pptx MIME — used when downloading the ORIGINAL (editable) file. */
const PPTX_MIME =
  "application/vnd.openxmlformats-officedocument.presentationml.presentation";

// Reuse the worker config the PDF viewer already sets at module load.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).href;

/**
 * A single AI-proposed change overlaid on a slide. The BE side computes
 * `bbox` (origin top-left, in slide-relative coords 0..1) so the FE can
 * position the highlight without a second roundtrip.
 *
 * Source of truth: the active `document_edit` action proposal payload
 * (BE ``DocumentEditPreview.changes[].bbox``). The parent surface
 * (``ArtifactPane``) reads `useActiveActionStore`, finds the proposal
 * targeting the open pptx, and forwards `documentEdit.changes` here.
 *
 * No separate `doc_edit_proposal` event — pptx review uses the single
 * `action_proposal` source-of-truth (May 24, 2026 unification).
 */
export interface PptxOverlayHighlight {
  /** Stable id — proposal_id + change index, used by the panel cross-ref. */
  readonly id: string;
  /** 1-indexed page (slide) number. */
  readonly slide: number;
  /** Bounding box in normalised page coords (0-1). */
  readonly bbox: { x: number; y: number; w: number; h: number };
  /** Color hint — `pending` (orange) / `accepted` (emerald) / `rejected` (rose). */
  readonly state?: "pending" | "accepted" | "rejected";
}

/**
 * Imperative handle the parent (ArtifactPane) hands to the review panel
 * so it can drive the viewer (scroll, accept/reject single, etc.). The
 * shape mirrors `XlsxEditorInlineHandle.scrollToAICell` / the DOCX
 * handle's `scrollToParaId` — uniform across mime types.
 */
export interface PptxViewerInlineHandle {
  /** Bring the slide containing `proposalId` into view + active. */
  scrollToProposal: (proposalId: string) => void;
  /** Currently-visible slide (1-indexed). */
  getCurrentSlide: () => number;
  /** Number of slides in the deck. */
  getSlideCount: () => number;
}

export interface PptxViewerInlineProps {
  /** Edit session — uses `session.read_url` which must point to the PDF
   *  preview generated by LibreOffice (synthesised by ArtifactPane). */
  readonly session: EditSession;
  /**
   * Overlay highlights to paint on top of slides. Parent derives these
   * from the active ``document_edit`` action proposal (BE-canonical
   * source). Empty array = no overlays.
   */
  readonly highlights: readonly PptxOverlayHighlight[];
  /** Optional 1-indexed slide to focus on mount. */
  readonly slideHint?: number;
  /**
   * Optional speaker notes by slide (1-indexed). When present and the
   * current slide has a note, the bottom pane shows it. Parent derives
   * notes from the proposal payload (e.g. `change.speaker_notes`).
   */
  readonly speakerNotesBySlide?: ReadonlyMap<number, string>;
  /**
   * URL of the ORIGINAL ``.pptx`` (not the PDF preview) so the download
   * button hands the user an editable PowerPoint, not a flattened PDF.
   * When omitted, download falls back to the rendered PDF bytes (legacy).
   * ArtifactPane passes ``getDocumentFileUrl(doc_id)``.
   */
  readonly downloadUrl?: string;
  /**
   * When provided, the toolbar shows a "Modifica con AI" button that seeds
   * the chat composer with an edit prompt for this deck (the user then adds
   * the specifics and sends → the agent proposes a ``document_edit`` and the
   * block outline editor takes over). ArtifactPane owns the chat wiring; the
   * viewer stays chat-agnostic.
   */
  readonly onEditWithAI?: () => void;
  /**
   * When provided, the toolbar shows a "Modifica" button that opens the deck's
   * block editor for MANUAL editing (titles/bullets/KPIs/layout, no AI) — the
   * parent swaps the viewer for ``ManualDeckEditMount``. Lightweight, on-brand
   * alternative to a heavy in-browser pptx editor.
   */
  readonly onEditManual?: () => void;
}

const PAGE_GUTTER_PX = 80; // breathing room around the slide canvas
/** Width reserved for the thumbnail strip on the left (rail + padding). */
const THUMB_RAIL_WIDTH = 112;
/** Thumbnail page width — small enough for ~10 visible at typical viewer
 *  height (~700px) without overcrowding the main slide canvas. */
const THUMB_PAGE_WIDTH = 88;
const OVERLAY_PADDING = 4; // px extra around the bbox so the highlight breathes

interface LoadedDoc {
  numPages: number;
}

export const PptxViewerInline = forwardRef<PptxViewerInlineHandle, PptxViewerInlineProps>(
  function PptxViewerInline(
    {
      session,
      highlights,
      slideHint,
      speakerNotesBySlide,
      downloadUrl,
      onEditWithAI,
      onEditManual,
    },
    ref,
  ) {
    const { i18n } = useLingui();
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const stageRef = useRef<HTMLDivElement | null>(null);
    const [doc, setDoc] = useState<LoadedDoc | null>(null);
    const [error, setError] = useState<Error | null>(null);
    const [bytes, setBytes] = useState<ArrayBuffer | null>(null);
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [currentSlide, setCurrentSlide] = useState<number>(slideHint ?? 1);
    // Phase 4.2 (May 2026) — transient "pulse" highlight that the
    // `<ReviewRail>` triggers when the user clicks "Vai" on a card. The
    // pulse overlays a brighter ring on top of the regular overlay box
    // for 3 seconds then auto-clears, giving the user an immediate cue
    // that the scroll target landed on the right change. Uses a
    // monotonically-incrementing token so successive jumps to the SAME
    // proposalId re-trigger the animation (re-keying the React subtree
    // for framer-motion's enter transition).
    const [pulse, setPulse] = useState<{ id: string; token: number } | null>(null);
    const pulseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Fetch PDF bytes through the auth bridge (Tauri Bearer header).
    useEffect(() => {
      const ctrl = new AbortController();
      setBytes(null);
      setError(null);
      fetchSessionBytes(session.read_url, { signal: ctrl.signal })
        .then(setBytes)
        .catch((err: unknown) => {
          if ((err as { name?: string }).name === "AbortError") return;
          setError(err instanceof Error ? err : new Error(String(err)));
        });
      return () => ctrl.abort();
    }, [session.read_url]);

    const file = useMemo(() => (bytes ? { data: bytes } : null), [bytes]);

    // ResizeObserver — keep the slide canvas width in sync with the stage.
    useEffect(() => {
      const el = stageRef.current;
      if (!el || typeof ResizeObserver === "undefined") return;
      const ro = new ResizeObserver((entries) => {
        const w = entries[0]?.contentRect.width ?? 0;
        setContainerWidth(Math.max(0, w - PAGE_GUTTER_PX));
      });
      ro.observe(el);
      return () => ro.disconnect();
    }, []);

    // ── Overlays grouped by slide ───────────────────────────────────────
    const overlaysBySlide = useMemo(() => {
      const out = new Map<number, PptxOverlayHighlight[]>();
      for (const h of highlights) {
        const arr = out.get(h.slide) ?? [];
        arr.push(h);
        out.set(h.slide, arr);
      }
      return out;
    }, [highlights]);

    const goToSlide = useCallback(
      (n: number) => {
        if (!doc) return;
        const target = Math.max(1, Math.min(doc.numPages, n));
        setCurrentSlide(target);
        const root = scrollRef.current;
        const el = root?.querySelector<HTMLElement>(`[data-slide-number="${target}"]`);
        el?.scrollIntoView({ block: "center", behavior: "smooth" });
      },
      [doc],
    );

    // Initial slide-hint jump
    useEffect(() => {
      if (!doc || !slideHint) return;
      goToSlide(slideHint);
    }, [doc, slideHint, goToSlide]);

    // Keyboard nav — ←/→ + PgUp/PgDn switch slides
    useEffect(() => {
      const root = stageRef.current;
      if (!root) return;
      const onKey = (e: KeyboardEvent): void => {
        if (!doc) return;
        // Only react when focus is inside the stage (no key-stealing from chat).
        if (!root.contains(document.activeElement)) return;
        if (e.key === "ArrowRight" || e.key === "PageDown") {
          e.preventDefault();
          goToSlide(currentSlide + 1);
        } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
          e.preventDefault();
          goToSlide(currentSlide - 1);
        }
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }, [currentSlide, doc, goToSlide]);

    // Pulse cleanup — auto-expire the transient highlight after 3s.
    // We keep the timer ref outside the imperative handle so a fresh
    // `scrollToProposal()` call resets the timer cleanly (cancel +
    // restart) instead of stacking multiple parallel pulses.
    useEffect(() => {
      if (!pulse) return;
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
      pulseTimerRef.current = setTimeout(() => {
        setPulse(null);
        pulseTimerRef.current = null;
      }, 3_000);
      return () => {
        if (pulseTimerRef.current) {
          clearTimeout(pulseTimerRef.current);
          pulseTimerRef.current = null;
        }
      };
    }, [pulse]);

    // ── Imperative handle ───────────────────────────────────────────────
    useImperativeHandle(
      ref,
      () => ({
        scrollToProposal: (proposalId: string) => {
          for (const [slide, overlays] of overlaysBySlide) {
            if (overlays.some((o) => o.id === proposalId)) {
              goToSlide(slide);
              // Trigger the 3s pulse highlight on the targeted overlay.
              // Increment the token so re-jumps to the same id re-fire
              // the animation (React keys off `${id}:${token}`).
              setPulse((prev) => ({
                id: proposalId,
                token: (prev?.token ?? 0) + 1,
              }));
              return;
            }
          }
        },
        getCurrentSlide: () => currentSlide,
        getSlideCount: () => doc?.numPages ?? 0,
      }),
      [currentSlide, doc?.numPages, goToSlide, overlaysBySlide],
    );

    // Download the ORIGINAL, EDITABLE .pptx — not the flattened PDF preview
    // (June 2026 user req: "quando faccio scarica deve scaricare PPTX e non
    // PDF"). We fetch the original bytes through the same auth bridge
    // (``fetchSessionBytes`` adds the Tauri Bearer header) and save them with
    // the real filename. Falls back to the in-memory PDF bytes only when no
    // ``downloadUrl`` was provided (legacy callers).
    const [downloading, setDownloading] = useState(false);
    const onDownload = useCallback(async () => {
      const triggerSave = (data: ArrayBuffer, name: string, mime: string) => {
        const blob = new Blob([data], { type: mime });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      };
      const baseName = session.filename ?? "slides";
      if (downloadUrl) {
        try {
          setDownloading(true);
          const original = await fetchSessionBytes(downloadUrl);
          const name = baseName.toLowerCase().endsWith(".pptx")
            ? baseName
            : `${baseName}.pptx`;
          triggerSave(original, name, PPTX_MIME);
          return;
        } catch {
          // fall through to the PDF fallback below
        } finally {
          setDownloading(false);
        }
      }
      if (bytes) {
        const name = baseName.toLowerCase().endsWith(".pdf")
          ? baseName
          : `${baseName}.pdf`;
        triggerSave(bytes, name, "application/pdf");
      }
    }, [bytes, downloadUrl, session.filename]);

    const slideWidth = containerWidth;
    const speakerNote = speakerNotesBySlide?.get(currentSlide) ?? null;

    return (
      <div className="flex h-full w-full flex-col bg-cream">
        {/* Toolbar — slide nav + dots + download */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-hairline border-b bg-cream/95 px-4 py-2 backdrop-blur">
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => goToSlide(currentSlide - 1)}
              disabled={!doc || currentSlide <= 1}
              aria-label={i18n._(msg`Slide precedente`)}
            >
              <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
            </Button>
            <span className="min-w-[68px] text-center font-medium text-[11px] text-ink-2 tabular-nums">
              {doc ? i18n._(msg`Slide ${currentSlide} / ${doc.numPages}`) : "—"}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => goToSlide(currentSlide + 1)}
              disabled={!doc || currentSlide >= doc.numPages}
              aria-label={i18n._(msg`Slide successiva`)}
            >
              <ChevronRight className="h-3.5 w-3.5" aria-hidden />
            </Button>
          </div>
          {doc && doc.numPages <= 24 ? (
            <SlideDots
              total={doc.numPages}
              current={currentSlide}
              changed={overlaysBySlide}
              onJump={goToSlide}
            />
          ) : (
            <span className="text-[10.5px] text-ink-3 tabular-nums">
              {overlaysBySlide.size > 0
                ? i18n._(msg`${overlaysBySlide.size} slide con modifiche`)
                : i18n._(msg`Nessuna modifica`)}
            </span>
          )}
          {onEditManual ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onEditManual}
              aria-label={i18n._(msg`Modifica`)}
              title={i18n._(msg`Modifica manuale (blocchi)`)}
              className="gap-1.5"
            >
              <Pencil className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
              <span className="font-medium text-[11px]">{i18n._(msg`Modifica`)}</span>
            </Button>
          ) : null}
          {onEditWithAI ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onEditWithAI}
              aria-label={i18n._(msg`Modifica con AI`)}
              title={i18n._(msg`Modifica con AI`)}
              className="gap-1.5 text-ando-orange"
            >
              <Sparkles className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
              <span className="font-medium text-[11px]">{i18n._(msg`Modifica con AI`)}</span>
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void onDownload()}
            disabled={(!bytes && !downloadUrl) || downloading}
            aria-label={
              downloadUrl
                ? i18n._(msg`Scarica PPTX`)
                : i18n._(msg`Scarica anteprima`)
            }
            title={
              downloadUrl
                ? i18n._(msg`Scarica PPTX (modificabile)`)
                : i18n._(msg`Scarica anteprima`)
            }
          >
            <Download className="h-3.5 w-3.5" aria-hidden />
          </Button>
        </div>

        {/* Stage — thumbnail rail + slide canvas + overlay layer.
            Thumbnails render INSIDE the same <Document> root so pdfjs
            loads the file once + reuses the parsed document across the
            big canvas + the thumbs (no duplicate fetch, single worker).
            Layout: 112px rail (left) · main canvas (flex-1). */}
        <div ref={stageRef} className="relative min-h-0 flex-1" >
          {/* Loading / error gate (June 2026): never hand ``<Document>`` a
              null ``file`` — react-pdf then paints its raw English
              "No PDF file specified." placeholder. Until the bytes land we
              show the Ando brand loader; on a fetch error we show a friendly
              message. Only once ``file`` AND the measured width are ready do
              we mount the PDF document. */}
          {error ? (
            <div className="grid h-full place-items-center px-6 text-center text-ink-3 text-sm">
              {i18n._(msg`Impossibile caricare le slide.`)}
              {error.message ? ` (${error.message})` : null}
            </div>
          ) : !file || slideWidth === 0 ? (
            <div className="grid h-full place-items-center">
              <AndoBrainLoader size="md" label={i18n._(msg`Preparazione slide…`)} />
            </div>
          ) : (
            <Document
              file={file}
              onLoadSuccess={({ numPages }) => {
                setError(null);
                setDoc({ numPages });
              }}
              onLoadError={(err) => setError(err)}
              loading={<div className="px-6 py-10 text-ink-3 text-sm">{i18n._(msg`Caricamento slides…`)}</div>}
              error={
                <div className="px-6 py-10 text-ink-3 text-sm">
                  {i18n._(msg`Impossibile caricare il file.`)}
                </div>
              }
            >
              <div className="absolute inset-0 flex">
                {/* Thumbnail rail — vertical strip on the left.
                    SOTA pattern (Notion AI / Gamma / Pitch). Each
                    thumb is a real <Page width={88}> so it's pixel-
                    accurate vs. a rasterised preview. Active slide
                    gets a 2px orange ring + tabular index below. */}
                {doc ? (
                  <SlideThumbnails
                    total={doc.numPages}
                    current={currentSlide}
                    changedSlides={overlaysBySlide}
                    onJump={goToSlide}
                  />
                ) : null}
                {/* Main slide scroll area */}
                <div
                  ref={scrollRef}
                  className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden bg-cream"
                >
                  {doc
                    ? Array.from({ length: doc.numPages }, (_, i) => i + 1).map((n) => (
                        <SlideCanvas
                          key={n}
                          slideNumber={n}
                          slideWidth={slideWidth - THUMB_RAIL_WIDTH}
                          overlays={overlaysBySlide.get(n) ?? []}
                          pulsedId={pulse?.id ?? null}
                          pulseToken={pulse?.token ?? 0}
                        />
                      ))
                    : null}
                </div>
              </div>
            </Document>
          )}
        </div>

        {/* Speaker notes pane */}
        {speakerNote ? (
          <aside className="shrink-0 border-hairline border-t bg-cream-50/80 px-4 py-3">
            <div className="mb-1 flex items-center gap-1.5 text-ink-3">
              <StickyNote className="h-3 w-3" aria-hidden />
              <span className="font-semibold text-[10px] uppercase tracking-[0.06em]">
                {i18n._(msg`Note relatore`)}
              </span>
            </div>
            <p className="text-[12px] text-ink-2 leading-snug">{speakerNote}</p>
          </aside>
        ) : null}
      </div>
    );
  },
);

// ── Thumbnail rail ──────────────────────────────────────────────────────

const SlideThumbnails: React.FC<{
  total: number;
  current: number;
  changedSlides: Map<number, readonly PptxOverlayHighlight[]>;
  onJump: (n: number) => void;
}> = ({ total, current, changedSlides, onJump }) => {
  const { i18n } = useLingui();
  return (
  <nav
    aria-label={i18n._(msg`Anteprime slide`)}
    className="flex shrink-0 flex-col overflow-y-auto border-hairline border-r bg-cream/60 py-2"
    style={{ width: THUMB_RAIL_WIDTH }}
  >
    {Array.from({ length: total }, (_, i) => i + 1).map((n) => {
      const isActive = n === current;
      const hasChanges = changedSlides.has(n);
      return (
        <button
          key={n}
          type="button"
          onClick={() => onJump(n)}
          aria-label={
            hasChanges
              ? i18n._(msg`Slide ${n} (con modifiche)`)
              : i18n._(msg`Slide ${n}`)
          }
          aria-current={isActive ? "true" : undefined}
          className={cn(
            "group mx-auto my-1.5 flex flex-col items-center gap-1 rounded-md px-1.5 py-1",
            "transition-[background-color,box-shadow] duration-150 ease-out",
            "hover:bg-ando-orange-soft/30",
            "focus-visible:bg-ando-orange-soft/40 focus-visible:outline-none",
          )}
        >
          <div
            className={cn(
              "relative overflow-hidden rounded-sm border bg-white",
              isActive
                ? "border-ando-orange ring-2 ring-ando-orange/30 ring-offset-1"
                : "border-hairline",
            )}
            style={{ width: THUMB_PAGE_WIDTH }}
          >
            <Page
              pageNumber={n}
              width={THUMB_PAGE_WIDTH}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              loading={
                <div
                  className="grid place-items-center text-[9px] text-ink-3"
                  style={{ height: THUMB_PAGE_WIDTH * 0.75 }}
                >
                  {n}
                </div>
              }
            />
            {hasChanges ? (
              <span
                aria-hidden
                className="absolute top-1 right-1 inline-block h-1.5 w-1.5 rounded-full bg-ando-orange shadow-[0_0_0_1.5px_white]"
              />
            ) : null}
          </div>
          <span
            className={cn(
              "font-medium text-[10px] tabular-nums",
              isActive ? "text-ando-orange" : "text-ink-3",
            )}
          >
            {n}
          </span>
        </button>
      );
    })}
  </nav>
  );
};
SlideThumbnails.displayName = "PptxViewerInline.SlideThumbnails";

// ── Slide canvas with overlay layer ─────────────────────────────────────

const SlideCanvas: React.FC<{
  slideNumber: number;
  slideWidth: number;
  overlays: readonly PptxOverlayHighlight[];
  pulsedId: string | null;
  pulseToken: number;
}> = ({ slideNumber, slideWidth, overlays, pulsedId, pulseToken }) => {
  return (
    <div
      data-slide-number={slideNumber}
      className="mx-auto my-6 max-w-full"
      style={{ width: slideWidth }}
    >
      <div className="relative">
        <Page
          pageNumber={slideNumber}
          width={slideWidth}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          className="shadow-[0_2px_10px_rgba(0,0,0,0.08)]"
        />
        {/* Overlay layer — absolute-positioned highlights */}
        {overlays.length > 0 ? (
          <div className="pointer-events-none absolute inset-0">
            {overlays.map((o) => (
              <OverlayBox
                // Re-key on `pulseToken` for the pulsed overlay so the
                // CSS animation re-fires on repeated jumps to the same id.
                key={o.id === pulsedId ? `${o.id}:pulse:${pulseToken}` : o.id}
                bbox={o.bbox}
                state={o.state ?? "pending"}
                pulsed={o.id === pulsedId}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const OverlayBox: React.FC<{
  bbox: { x: number; y: number; w: number; h: number };
  state: NonNullable<PptxOverlayHighlight["state"]>;
  pulsed?: boolean;
}> = ({ bbox, state, pulsed }) => {
  const stateStyle: Record<
    NonNullable<PptxOverlayHighlight["state"]>,
    { borderClass: string; bgClass: string }
  > = {
    pending: { borderClass: "border-ando-orange/70", bgClass: "bg-ando-orange/[0.10]" },
    accepted: { borderClass: "border-emerald-500/70", bgClass: "bg-emerald-500/[0.10]" },
    rejected: { borderClass: "border-rose-500/60", bgClass: "bg-rose-500/[0.08]" },
  };
  const s = stateStyle[state];
  return (
    <div
      aria-hidden
      data-pulsed={pulsed ? "true" : undefined}
      className={cn(
        "absolute rounded-md border-2 transition-opacity duration-200 ease-out",
        s.borderClass,
        s.bgClass,
        // 3s pulse — orange ring grows + fades. Uses an inline keyframe
        // via Tailwind's `animate-[name_3s_ease-out_1]` arbitrary syntax
        // so we don't need a global keyframe registration for this
        // single-use animation. `prefers-reduced-motion` falls back to
        // a steady ring (no movement).
        pulsed &&
          "ring-2 ring-ando-orange/70 ring-offset-2 ring-offset-cream motion-safe:animate-[pptx-pulse_3s_ease-out_1]",
      )}
      style={{
        left: `calc(${bbox.x * 100}% - ${OVERLAY_PADDING}px)`,
        top: `calc(${bbox.y * 100}% - ${OVERLAY_PADDING}px)`,
        width: `calc(${bbox.w * 100}% + ${OVERLAY_PADDING * 2}px)`,
        height: `calc(${bbox.h * 100}% + ${OVERLAY_PADDING * 2}px)`,
      }}
    />
  );
};

// ── Slide dots (≤24 slides) ─────────────────────────────────────────────

const SlideDots: React.FC<{
  total: number;
  current: number;
  changed: Map<number, readonly PptxOverlayHighlight[]>;
  onJump: (n: number) => void;
}> = ({ total, current, changed, onJump }) => {
  const { i18n } = useLingui();
  return (
  <div className="flex items-center gap-1" role="tablist" aria-label={i18n._(msg`Naviga slide`)}>
    {Array.from({ length: total }, (_, i) => i + 1).map((n) => {
      const isActive = n === current;
      const hasChanges = changed.has(n);
      return (
        <button
          key={n}
          type="button"
          role="tab"
          aria-selected={isActive}
          aria-label={
            hasChanges
              ? i18n._(msg`Slide ${n} — ha modifiche`)
              : i18n._(msg`Slide ${n}`)
          }
          onClick={() => onJump(n)}
          className={cn(
            "inline-flex h-2 w-2 items-center justify-center rounded-full transition-all duration-150 ease-out",
            isActive
              ? "w-4 bg-ando-orange"
              : hasChanges
                ? "bg-ando-orange/40 hover:bg-ando-orange/70"
                : "bg-ink-1/15 hover:bg-ink-1/30",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 focus-visible:ring-offset-1",
          )}
        />
      );
    })}
  </div>
  );
};
